import React, { useEffect, useContext } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SessionExpirationNotification.css';
import { onSessionExpired, clearSessionExpiredCallback } from '../../services/sessionExpirationService';
import { ServiceContext } from '@msx/platform-services';

/**
 * SessionExpirationNotification Component
 * Displays a notification when the user's session has expired
 * Prompts the user to sign in again
 */
export const SessionExpirationNotification: React.FC = () => {
  const serviceContext = useContext(ServiceContext);
  const authContext = (serviceContext as any)?.authContext;

  useEffect(() => {
    // Early return if authContext is not available
    if (!authContext) {
      return undefined;
    }

    // Define the callback
    const handleSessionExpired = () => {
      toast.error(
        <div role="alert" aria-live="assertive">
          <strong>Session Expired</strong>
          <p>Your session has expired. Please sign in again to continue.</p>
        </div>,
        {
          position: 'top-center',
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          closeButton: true,
          toastId: 'session-expired',
          className: 'session-expired-toast',
          onClose: () => {
            // Trigger sign-in when user dismisses the notification
            authContext.loginRedirect();
          },
        }
      );
    };

    // Register callback for session expiration
    onSessionExpired(handleSessionExpired);

    // Cleanup on unmount
    return () => {
      clearSessionExpiredCallback(handleSessionExpired);
    };
  }, [authContext]);

  return (
    <ToastContainer
      position="top-center"
      containerId="session-notification"
      className="session-notification-container"
    />
  );
};
